// src/components/services/ServiceTemplate.jsx
import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Link } from 'react-router-dom';

const ServiceTemplate = ({ 
  title, 
  badge, 
  description, 
  heroImage, 
  features, 
  benefits, 
  technologies, 
  industries,
  process,
  ctaText = "Get Started",
  ctaLink = "/contact"
}) => {
  const { isDark } = useTheme();

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className={`relative py-16 sm:py-20 lg:py-24 overflow-hidden ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt={title}
            className="w-full h-full object-cover object-center"
          />
          <div className={`absolute inset-0 ${
            isDark 
              ? 'bg-gradient-to-r from-black/85 via-black/70 to-black/50' 
              : 'bg-gradient-to-r from-black/85 via-black/30 to-black/10' 
          }`} />
        </div>

        {isDark && (
          <>
            <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-blue-500/15 rounded-full blur-3xl z-0" />
            <div className="absolute bottom-[-250px] right-[-150px] w-[600px] h-[600px] bg-blue-600/15 rounded-full blur-3xl z-0" />
          </>
        )}
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-4">
              <span className={`text-xs sm:text-sm tracking-[0.2em] font-medium px-4 py-2 rounded-full inline-block backdrop-blur-sm ${
                isDark 
                  ? 'text-blue-400 bg-blue-500/20 border border-blue-500/30' 
                  : 'text-blue-600 bg-blue-50 border border-blue-200'
              }`}>
                {badge}
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight text-white">
              {title}
            </h1>
            
            <p className={`text-lg sm:text-xl leading-relaxed ${isDark ? 'text-gray-200' : 'text-white'}`}>
              {description}
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Service <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Overview</span>
            </h2>
            <p className={`text-lg max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Comprehensive solutions designed to address your specific business challenges
            </p>
          </div>

          <div className={`p-8 rounded-2xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
            <p className={`text-lg leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {description}
            </p>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-gray-900/30' : 'bg-blue-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Key <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              What makes our service stand out
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`group p-6 rounded-2xl transition-all duration-300 ${
                  isDark 
                    ? 'bg-white/5 border border-white/10 hover:border-blue-500/30 hover:bg-white/10' 
                    : 'bg-white border border-gray-200 hover:border-blue-400 hover:shadow-lg'
                }`}
              >
                <div className="text-4xl mb-4">{feature.icon}</div>

                <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {feature.title}
                </h3>

                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            <div>
              <h2 className={`text-3xl sm:text-4xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Why Choose Our{' '}
                <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                  Service
                </span>
              </h2>

              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">

                    <svg 
                      className={`w-6 h-6 mt-0.5 flex-shrink-0 ${
                        isDark ? 'text-blue-400' : 'text-blue-600'
                      }`} 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path 
                        fillRule="evenodd" 
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                        clipRule="evenodd" 
                      />
                    </svg>

                    <span className={`text-lg ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {benefit}
                    </span>

                  </li>
                ))}
              </ul>
            </div>

            <div className={`p-8 rounded-2xl ${
              isDark 
                ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/10 border border-white/10' 
                : 'bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-100'
            }`}>
              <div className="text-center">
                <div className="text-6xl mb-4">💡</div>

                <h3 className={`text-2xl font-bold mb-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Transform Your Business
                </h3>

                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Leverage our expertise to drive growth and efficiency
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-gray-900/30' : 'bg-blue-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Technologies &{' '}
              <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                Capabilities
              </span>
            </h2>

            <p className={`text-lg max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Modern tools and practices we use to deliver excellence
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {technologies.map((tech, index) => (
              <div 
                key={index} 
                className={`text-center p-4 rounded-xl ${
                  isDark 
                    ? 'bg-white/5 border border-white/10' 
                    : 'bg-white border border-gray-200'
                }`}
              >
                <div className="text-2xl mb-2">{tech.icon}</div>

                <span className={`text-sm font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {tech.name}
                </span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Industries We Serve */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className={`rounded-3xl p-8 md:p-12 text-center ${
            isDark 
              ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/10 border border-white/10' 
              : 'bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-100'
          }`}>

            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Industries We{' '}
              <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                Serve
              </span>
            </h2>

            <p className={`text-lg mb-8 max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Our solutions are tailored for various sectors
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              {industries.map((industry, index) => (
                <span 
                  key={index} 
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    isDark 
                      ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {industry}
                </span>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className={`py-16 sm:py-20 ${
        isDark ? 'bg-gray-900/30' : 'bg-blue-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Our{' '}
              <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                Process
              </span>
            </h2>

            <p className={`text-lg max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              How we bring your vision to life
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step, index) => (
              <div key={index} className="relative">

                <div className={`text-center p-6 rounded-2xl ${
                  isDark 
                    ? 'bg-white/5 border border-white/10' 
                    : 'bg-white border border-gray-200'
                }`}>

                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xl">
                    {index + 1}
                  </div>

                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {step.title}
                  </h3>

                  <p className={`text-sm ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {step.description}
                  </p>

                </div>

                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-2xl text-blue-400">
                    →
                  </div>
                )}

              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Ready to Get Started?
          </h2>

          <p className={`mb-8 ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Let's discuss how our {title.toLowerCase()} can transform your business
          </p>

          <Link to={ctaLink}>
            <button className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
              {ctaText}
            </button>
          </Link>

        </div>
      </section>
    </div>
  );
};

export default ServiceTemplate;

